/**
 * Represents a service area with its table configuration.
 */
export type ServiceArea = Readonly<{
  name: string;
  tableCount: number;
  prefix: string;
}>;

/**
 * Configuration for table service — areas and their tables.
 * Immutable by design, created only via factory function.
 */
export type TableServiceConfig = Readonly<{
  hasTableService: boolean;
  areas: ReadonlyArray<ServiceArea>;
}>;

/**
 * Creates a validated ServiceArea value object.
 *
 * @param name - Area name (e.g., "Terraza", "Interior")
 * @param tableCount - Number of tables in this area
 * @param prefix - Table name prefix (e.g., "T", "TE")
 * @returns Frozen ServiceArea
 * @throws Error if name is empty or tableCount is invalid
 */
export const createServiceArea = (name: string, tableCount: number, prefix: string): ServiceArea => {
  if (!name.trim()) {
    throw new Error('Service area name is required');
  }
  if (!Number.isInteger(tableCount) || tableCount < 1) {
    throw new Error(`Table count must be a positive integer, received: ${tableCount}`);
  }
  if (!prefix.trim()) {
    throw new Error('Service area prefix is required');
  }
  return Object.freeze({ name: name.trim(), tableCount, prefix: prefix.trim() });
};

/**
 * Creates a validated TableServiceConfig value object.
 *
 * @param params - Raw configuration parameters
 * @returns Frozen TableServiceConfig
 * @throws Error if table service is enabled but no areas are provided
 *
 * @example
 * const config = createTableServiceConfig({
 *   hasTableService: true,
 *   areas: [{ name: 'Terraza', tableCount: 10, prefix: 'TE' }],
 * });
 */
export const createTableServiceConfig = (params: {
  hasTableService: boolean;
  areas: ReadonlyArray<{ name: string; tableCount: number; prefix: string }>;
}): TableServiceConfig => {
  const areas = params.hasTableService
    ? params.areas.map((area) => createServiceArea(area.name, area.tableCount, area.prefix))
    : [];

  return Object.freeze({
    hasTableService: params.hasTableService,
    areas: Object.freeze(areas),
  });
};
